import { useTranslate } from '@refinedev/core';
import { Descriptions, Divider, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationAudioOut,
  PublicationDetailOut,
  PublicationEbookOut,
} from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import {
  formatFileSizeComma,
  formatMinutesVerbose,
  formatThousands,
} from '@/utils/format';
import { publicationPublishingStatusColors } from '../../publicationColors';

const { Text } = Typography;

type FormatKind = 'ebook' | 'audio';

interface DrawerFormatsTabProps {
  record: PublicationDetailOut;
}

const FormatSection = ({
  kind,
  data,
}: {
  kind: FormatKind;
  data: PublicationEbookOut | PublicationAudioOut;
}) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const T = 'user_catalog.publications.show';
  const F = 'user_catalog.publications.ebook.fields';
  const ebook = kind === 'ebook' ? (data as PublicationEbookOut) : null;

  const items: DescriptionsProps['items'] = [
    {
      key: 'product_id',
      label: t('user_catalog.publications.drawer.product_id'),
      children: <Text code>{data.id}</Text>,
    },
    {
      key: 'version',
      label: t(`${F}.version`),
      children: orDash(data.version),
    },
    {
      key: 'publishing_status',
      label: t(`${F}.publishing_status`),
      children: (
        <Tag
          color={
            publicationPublishingStatusColors[data.publishing_status] ??
            'default'
          }
        >
          {t(
            `user_catalog.publications.publishing_status.${data.publishing_status}`
          )}
        </Tag>
      ),
    },
    {
      key: 'mod_status',
      label: t('base.fields.mod_status'),
      children: <ModStatusTag value={data.mod_status} />,
    },
    {
      key: 'isbn',
      label: t(`${F}.isbn`),
      children: orDash(data.isbn),
    },
    {
      key: 'ean',
      label: t(`${F}.ean`),
      children: orDash(data.ean),
    },
    {
      key: 'edition',
      label: t(`${F}.edition`),
      children: orDash(data.edition),
    },
    ...(ebook
      ? ([
          {
            key: 'pages_count',
            label: t(`${F}.pages_count`),
            children: orDash(formatThousands(ebook.pages_count)),
          },
        ] as NonNullable<DescriptionsProps['items']>)
      : []),
    {
      key: 'duration',
      label: t(`${T}.fields.duration_time`),
      children: orDash(formatMinutesVerbose(data.duration_minutes)),
    },
    {
      key: 'files_size',
      label: t(`${T}.fields.files_size`),
      children: orDash(formatFileSizeComma(data.files_size_bytes)),
    },
    {
      key: 'source_id',
      label: t(`${F}.source_id`),
      children:
        data.source_id != null ? (
          <ShowIconLink resource="content_catalog.products" id={data.source_id}>
            <Text code>{data.source_id}</Text>
          </ShowIconLink>
        ) : (
          orDash(null)
        ),
    },
  ];

  return (
    <>
      <Divider orientation="left" orientationMargin={0}>
        {t(`user_catalog.publications.tabs.${kind}`)}
      </Divider>
      <Descriptions {...verticalDescProps} column={3} items={items} />
    </>
  );
};

export const DrawerFormatsTab = ({ record }: DrawerFormatsTabProps) => {
  if (!record.ebook && !record.audio) return <>{orDash(null)}</>;

  return (
    <>
      {record.ebook && <FormatSection kind="ebook" data={record.ebook} />}
      {record.audio && <FormatSection kind="audio" data={record.audio} />}
    </>
  );
};
